import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { MessageSquarePlus, Send, Loader2, Bug, Lightbulb, ThumbsUp, Paperclip, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

type FeedbackType = 'bug' | 'feature' | 'general';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'application/pdf'];

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast({ title: 'File too large', description: 'Maximum size is 10MB.', variant: 'destructive' });
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Unsupported file type', description: 'Please attach an image, video, or PDF.', variant: 'destructive' });
      return;
    }
    setAttachment(file);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({
        title: 'Please enter your feedback',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      let attachmentUrl: string | null = null;

      // Upload attachment if present
      if (attachment) {
        const ext = attachment.name.split('.').pop() || 'bin';
        const filePath = `${user?.id || 'anon'}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('feedback-attachments')
          .upload(filePath, attachment, { contentType: attachment.type, upsert: false });

        if (uploadError) {
          console.error('Attachment upload failed:', uploadError);
          toast({ title: 'Failed to upload attachment', variant: 'destructive' });
          setSubmitting(false);
          return;
        }

        const { data: urlData } = supabase.storage.from('feedback-attachments').getPublicUrl(filePath);
        attachmentUrl = urlData.publicUrl;
      }

      // Store feedback in database
      const { error } = await (supabase.from('feedback' as any) as any).insert({
        user_id: user?.id || null,
        type,
        message: message.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        attachment_url: attachmentUrl,
      });

      if (error) throw error;

      // Send email notification to admin (fire and forget)
      supabase.functions.invoke('send-feedback-notification', {
        body: {
          type,
          message: message.trim(),
          userEmail: user?.email,
          pageUrl: window.location.href,
        },
      }).catch((err) => console.error('Failed to send notification:', err));

      toast({
        title: 'Thank you!',
        description: 'Your feedback has been submitted.',
      });
      setMessage('');
      setType('general');
      setAttachment(null);
      setOpen(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Submission failed',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: Bug, description: 'Something isn\'t working' },
    { value: 'feature', label: 'Feature Request', icon: Lightbulb, description: 'Suggest an improvement' },
    { value: 'general', label: 'General Feedback', icon: ThumbsUp, description: 'Share your thoughts' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="fixed bottom-4 right-4 z-50 gap-2 shadow-lg bg-card hover:bg-accent border-border"
        >
          <MessageSquarePlus className="w-4 h-4" />
          <span className="hidden sm:inline">Feedback</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="w-5 h-5 text-primary" />
            Share Your Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve Sellaris. We read every submission!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-3">
            <Label>What type of feedback?</Label>
            <RadioGroup
              value={type}
              onValueChange={(v) => setType(v as FeedbackType)}
              className="grid grid-cols-3 gap-2"
            >
              {feedbackTypes.map((ft) => (
                <Label
                  key={ft.value}
                  htmlFor={ft.value}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border cursor-pointer transition-all ${
                    type === ft.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <RadioGroupItem value={ft.value} id={ft.value} className="sr-only" />
                  <ft.icon className={`w-5 h-5 ${type === ft.value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="text-xs font-medium text-center">{ft.label}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message">Your feedback</Label>
            <Textarea
              id="feedback-message"
              placeholder={
                type === 'bug'
                  ? 'Describe the issue you encountered...'
                  : type === 'feature'
                  ? 'What feature would help you the most?'
                  : 'Tell us what you think...'
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/2000</p>
          </div>

          {/* Attachment */}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,application/pdf"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="w-4 h-4" />
              Attach file
            </Button>
            {attachment && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                <span className="truncate max-w-[150px]">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="hover:text-foreground">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
            className="w-full gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
